import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Popup } from "../design_system/components/Popup/Popup";
import { Icons } from "../design_system/components/Icons";
import { Loader } from "../design_system/components/Loader";
import { switchCurrentTeam } from "../actions/teamSliceActions/actions";
import { dismissTeamConflict, updateLastLoadedPath, resetTeamSwitch } from "../redux/currentTeamSlice";
import { checkTeamContext, listenForTeamChanges } from "./teamSwitchService";
import { matchesResource, recordRoute } from "./routes";
import TeamLoadError from "./TeamLoadError";
import "./teamContext.scss";

export default function TeamSwitchPopup() {
  const user = useSelector(state => state.current_user.user);
  const { team, teamConflict: conflict, teamSwitch: switching } = useSelector(state => state.current_team);
  const dispatch = useDispatch(), history = useHistory(), location = useLocation();

  useEffect(() => listenForTeamChanges(history), [history]);
  useEffect(() => { dispatch(resetTeamSwitch()); }, [user.id, dispatch]);
  useEffect(() => {
    if (!user.is_super_member || !team.id) return;
    const path = location.pathname + location.search + location.hash;
    if (!recordRoute(path)) dispatch(updateLastLoadedPath(path));
    checkTeamContext();
  }, [user.is_super_member, team.id, location, dispatch]);

  const retry = () => dispatch(switchCurrentTeam({ id: switching.teamId }, { destination: switching.destination }));
  if (!conflict) {
    if (!switching) return null;
    return <div className="team-context-loading" role="status" aria-label="Switching teams" aria-busy={!switching.error}>
      {switching.error ? <TeamLoadError onRetry={retry} /> : <Loader Size="Large" isFullPage={true} />}
    </div>;
  }
  if (!user.is_super_member || conflict.userId !== user.id || String(conflict.data.request_team_id) !== String(team.id)) return null;
  const { data, cancelPath } = conflict;
  const path = location.pathname + location.search + location.hash;
  const isRequestedRecord = matchesResource(path, data.resource);
  const busy = Boolean(switching && !switching.error);
  const cancel = () => {
    if (busy) return;
    dispatch(dismissTeamConflict());
    if (isRequestedRecord) history.replace(cancelPath);
  };
  const confirm = () => {
    if (busy) return;
    dispatch(switchCurrentTeam({ id: data.target_team.id }, { destination: isRequestedRecord ? path : data.resource_path }));
  };
  return <Popup name={`Switch to ${data.target_team.name}?`}
    customClassName="team-switch-popup" showHeaderDivider={false}
    handleOnClosePopup={busy ? undefined : cancel}
    secondaryAction={{ label: "Cancel", onClick: cancel, disabled: busy }}
    primaryAction={{ label: "Switch & Open", onClick: confirm, isLoading: busy, disabled: busy }}>
    <div className="team-switch-popup__body body-m">
      <p>This link is from {data.target_team.name}. You’re currently in {team.name}. Switch teams to open it.</p>
      <p className="team-switch-popup__info body-s"><Icons name="info" size={16} aria-hidden="true" /><span>Any other tabs you have open will switch to this team too.</span></p>
      {switching?.error && <p className="team-switch-popup__error body-s" role="alert">{switching.error}</p>}
    </div>
  </Popup>;
}
